import React, { createContext, useContext, useMemo } from 'react';
import { DICTIONARIES, en } from './dictionaries.js';

/**
 * SB-8809 — widget-chrome localization.
 *
 * The API resolves the effective language (widget `language` attribute wins
 * over the embed's stored default) and echoes it in the async-data payload as
 * a canonical Sessionboard locale code (e.g. 'de', 'fr_CA', 'pt_BR').
 * Chrome dictionaries exist for en/de/fr/es; any other locale falls back to
 * English chrome while dates and organizer content still localize.
 */

/**
 * Canonical Sessionboard locale ('de', 'fr_CA') → dictionary language.
 * @param {string | null | undefined} locale
 */
export const toDictionaryLanguage = locale => {
  if (!locale || typeof locale !== 'string') return 'en';
  const primary = locale.replace(/-/g, '_').split('_')[0].toLowerCase();
  return DICTIONARIES[primary] ? primary : 'en';
};

/**
 * Canonical Sessionboard locale → BCP-47 tag for Intl APIs
 * ('de' → 'de', 'pt_BR' → 'pt-BR'). Falls back to 'en-US'.
 * @param {string | null | undefined} locale
 */
export const toBcp47 = locale => {
  if (!locale || typeof locale !== 'string') return 'en-US';
  const cleaned = locale.trim().replace(/_/g, '-');
  try {
    // Throws on junk — never hand Intl an invalid tag.
    return Intl.getCanonicalLocales(cleaned)[0] || 'en-US';
  } catch (_err) {
    return 'en-US';
  }
};

const interpolate = (template, params) => {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(params, key) ? String(params[key]) : match
  );
};

/**
 * Build a translator for a canonical Sessionboard locale.
 * `t(key, params)` — plain lookup with {param} interpolation.
 * `t.plural(baseKey, count, params)` — picks `${baseKey}_one` / `_other`
 * (fr counts 0 and 1 as "one"; en/de/es use exactly 1).
 * @param {string | null | undefined} locale
 */
export const createTranslator = locale => {
  const language = toDictionaryLanguage(locale);
  const dictionary = DICTIONARIES[language] || en;

  const t = (key, params) => {
    const template = dictionary[key] ?? en[key] ?? key;
    return interpolate(template, params);
  };

  t.plural = (baseKey, count, params) => {
    const isOne = language === 'fr' ? count === 0 || count === 1 : count === 1;
    return t(`${baseKey}_${isOne ? 'one' : 'other'}`, { count, ...params });
  };

  t.language = language;
  return t;
};

const defaultValue = {
  t: createTranslator(null),
  locale: null,
  bcp47: 'en-US',
};

export const LocaleContext = createContext(defaultValue);

/**
 * Provides the translator + BCP-47 tag for the resolved embed language.
 * Components render fine without a provider (English chrome).
 * @param {{ locale: string | null | undefined, children: React.ReactNode }} props
 */
export const LocaleProvider = ({ locale, children }) => {
  const value = useMemo(
    () => ({
      t: createTranslator(locale),
      locale: locale || null,
      bcp47: toBcp47(locale),
    }),
    [locale]
  );
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

/** Hook for widget chrome: `const { t, bcp47 } = useI18n();` */
export const useI18n = () => useContext(LocaleContext);
